import React, { useState, useEffect } from 'react';
import {
  CheckCircle,
  Calendar,
  CreditCard,
  ArrowRight,
  Zap,
  Shield,
  Star,
  Clock
} from 'lucide-react';

function SubscriptionSuccess({ user, onContinueToOnboarding }) {
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [subscriptionInfo, setSubscriptionInfo] = useState(null);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    // Extraer información de la suscripción
    if (user) {
      const nextPayment = user.next_payment_date 
        ? new Date(user.next_payment_date)
        : new Date(Date.now() + 30 * 24 * 60 * 60 * 1000); // 30 días desde ahora

      setSubscriptionInfo({
        plan: 'Constructora Pro',
        price: '$10,000',
        currency: 'ARS',
        nextPaymentDate: nextPayment.toLocaleDateString('es-AR', {
          weekday: 'long',
          year: 'numeric',
          month: 'long',
          day: 'numeric'
        }),
        startDate: new Date().toLocaleDateString('es-AR', {
          year: 'numeric',
          month: 'long',
          day: 'numeric'
        })
      });
    }
  }, [user]);

  const features = [
    { icon: Zap, title: 'Acceso Completo', desc: 'Todas las funcionalidades desbloqueadas' },
    { icon: Shield, title: 'Soporte Premium', desc: 'Atención prioritaria 24/7' },
    { icon: Star, title: 'Actualizaciones', desc: 'Nuevas funciones automáticamente' },
    { icon: Calendar, title: 'Sin Límites', desc: 'Proyectos y usuarios ilimitados' }
  ];

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: isMobile ? '20px' : '40px'
    }}>
      <div style={{
        background: 'rgba(255, 255, 255, 0.95)',
        backdropFilter: 'blur(20px)',
        borderRadius: '24px',
        padding: isMobile ? '32px 24px' : '48px 40px',
        maxWidth: '600px',
        width: '100%',
        boxShadow: '0 20px 40px rgba(0, 0, 0, 0.1)',
        border: '1px solid rgba(255, 255, 255, 0.2)',
        textAlign: 'center'
      }}>
        {/* Header Success */}
        <div style={{
          width: '80px',
          height: '80px',
          background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          margin: '0 auto 24px',
          animation: 'pulse 2s infinite'
        }}>
          <CheckCircle size={40} color="white" />
        </div>

        <h1 style={{
          fontSize: isMobile ? '28px' : '32px',
          fontWeight: '700',
          color: '#1e293b',
          margin: '0 0 12px 0',
          fontFamily: 'Inter, sans-serif'
        }}>
          ¡Pago Exitoso! 🎉
        </h1>

        <p style={{
          fontSize: '18px',
          color: '#64748b',
          margin: '0 0 32px 0',
          lineHeight: '1.6'
        }}>
          Tu suscripción automática a <strong>Constructora Pro</strong> está ahora activa. Mercado Pago cobrará automáticamente cada mes.
        </p>

        {/* Subscription Details */}
        {subscriptionInfo && (
          <div style={{
            background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
            borderRadius: '16px',
            padding: '24px',
            margin: '0 0 32px 0',
            border: '1px solid #e2e8f0'
          }}>
            <h3 style={{
              fontSize: '18px',
              fontWeight: '600',
              color: '#1e293b',
              margin: '0 0 16px 0'
            }}>
              Detalles de tu Suscripción
            </h3>

            <div style={{
              display: 'grid',
              gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
              gap: '16px',
              textAlign: 'left'
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px'
              }}>
                <div style={{
                  width: '40px',
                  height: '40px',
                  background: '#3b82f6',
                  borderRadius: '8px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <Star size={20} color="white" />
                </div>
                <div>
                  <div style={{ fontWeight: '600', color: '#1e293b' }}>Plan Activo</div>
                  <div style={{ color: '#64748b', fontSize: '14px' }}>Constructora Pro</div>
                </div>
              </div>

              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px'
              }}>
                <div style={{
                  width: '40px',
                  height: '40px',
                  background: '#10b981',
                  borderRadius: '8px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <Calendar size={20} color="white" />
                </div>
                <div>
                  <div style={{ fontWeight: '600', color: '#1e293b' }}>Inicio</div>
                  <div style={{ color: '#64748b', fontSize: '14px' }}>{subscriptionInfo.startDate}</div>
                </div>
              </div>

              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px'
              }}>
                <div style={{
                  width: '40px',
                  height: '40px',
                  background: '#f59e0b',
                  borderRadius: '8px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <Clock size={20} color="white" />
                </div>
                <div>
                  <div style={{ fontWeight: '600', color: '#1e293b' }}>Próximo Cobro</div>
                  <div style={{ color: '#64748b', fontSize: '14px' }}>{subscriptionInfo.nextPaymentDate}</div>
                </div>
              </div>

              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px'
              }}>
                <div style={{
                  width: '40px',
                  height: '40px',
                  background: '#8b5cf6',
                  borderRadius: '8px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <CreditCard size={20} color="white" />
                </div>
                <div>
                  <div style={{ fontWeight: '600', color: '#1e293b' }}>Precio Mensual</div>
                  <div style={{ color: '#64748b', fontSize: '14px' }}>$10,000 ARS/mes</div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Important Info */}
        <div style={{
          background: 'linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%)',
          borderRadius: '12px',
          padding: '16px',
          margin: '0 0 32px 0',
          border: '1px solid #93c5fd'
        }}>
          <p style={{
            margin: 0,
            fontSize: '14px',
            color: '#1e40af',
            fontWeight: '500'
          }}>
            <strong>🔄 Cobro Automático:</strong> Mercado Pago cobrará automáticamente $10,000 ARS cada mes usando el mismo método de pago. No necesitás hacer nada más.
          </p>
        </div>

        {/* Features Grid */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
          gap: '16px',
          margin: '0 0 40px 0'
        }}>
          {features.map((feature, index) => (
            <div key={index} style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              padding: '12px',
              background: 'white',
              borderRadius: '12px',
              border: '1px solid #f1f5f9'
            }}>
              <div style={{
                width: '32px',
                height: '32px',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                borderRadius: '8px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <feature.icon size={16} color="white" />
              </div>
              <div style={{ textAlign: 'left' }}>
                <div style={{ fontWeight: '600', fontSize: '14px', color: '#1e293b' }}>
                  {feature.title}
                </div>
                <div style={{ fontSize: '12px', color: '#64748b' }}>
                  {feature.desc}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Action Buttons */}
        <div style={{
          display: 'flex',
          flexDirection: isMobile ? 'column' : 'row',
          gap: '16px',
          justifyContent: 'center'
        }}>
          <button
            onClick={onContinueToOnboarding}
            style={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white',
              border: 'none',
              borderRadius: '12px',
              padding: '16px 32px',
              fontSize: '16px',
              fontWeight: '600',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
              transition: 'transform 0.2s ease, box-shadow 0.2s ease',
              fontFamily: 'Inter, sans-serif'
            }}
            onMouseEnter={e => {
              e.target.style.transform = 'translateY(-2px)';
              e.target.style.boxShadow = '0 8px 25px rgba(102, 126, 234, 0.3)';
            }}
            onMouseLeave={e => {
              e.target.style.transform = 'translateY(0)';
              e.target.style.boxShadow = 'none';
            }}
          >
            Continuar al Dashboard
            <ArrowRight size={20} />
          </button>
        </div>

        <p style={{
          fontSize: '12px',
          color: '#94a3b8',
          margin: '24px 0 0 0',
          lineHeight: '1.4'
        }}>
          Podés cancelar tu suscripción en cualquier momento desde configuración. 
          No se realizarán cobros adicionales sin previo aviso.
        </p>
      </div>

      <style jsx>{`
        @keyframes pulse {
          0%, 100% {
            transform: scale(1);
          }
          50% {
            transform: scale(1.05);
          }
        }
      `}</style>
    </div>
  );
}

export default SubscriptionSuccess; 